import React from 'react';

/**
 * @param {Object} props
 * @param {string} props.label
 * @param {number} [props.value]
 * @param {(value: number) => void} props.onChange
 * @param {string} [props.className]
 * @param {boolean} [props.inline]
 * @returns {React.ReactElement}
 */
const NumberField = ({ label, value, onChange, className, inline }) => {
  const input = (
    <input
      className={inline ? className : undefined}
      type="number"
      onChange={(event) => onChange(parseFloat(event.target.value) || 0)}
      value={value || 0}
    />
  );

  if (inline) {
    return (
      <div>
        {label.toLowerCase()} {input}
      </div>
    );
  }

  return (
    <label className={className}>
      <span>{label}</span>
      {input}
    </label>
  );
};

/**
 * @param {Object} props
 * @param {import('actions/scene.types').CalendarShape} props.shape Calendar shape being edited.
 * @param {import('actions/scene.types').CalendarShapeChangeHandler} props.onChange Applies a partial calendar update.
 * @param {string} [props.fieldClassName]
 * @param {boolean} [props.inline]
 * @param {React.ReactNode} [props.separator]
 * @param {boolean} [props.showPadding]
 * @param {boolean} [props.showDayMargin]
 * @param {string} [props.paddingLabel]
 * @returns {React.ReactElement}
 */
export const CalendarSizeFields = ({
  shape,
  onChange,
  fieldClassName,
  inline = false,
  separator = null,
  showPadding = true,
  showDayMargin = true,
  paddingLabel = 'Padding',
}) => {
  const paddingFields = (
    <>
      <NumberField
        className={fieldClassName}
        inline={inline}
        label="Left"
        value={shape.calendarShapeSize?.paddingLeft}
        onChange={(paddingLeft) => onChange({ calendarShapeSize: { paddingLeft } })}
      />
      <NumberField
        className={fieldClassName}
        inline={inline}
        label="Right"
        value={shape.calendarShapeSize?.paddingRight}
        onChange={(paddingRight) => onChange({ calendarShapeSize: { paddingRight } })}
      />
      <NumberField
        className={fieldClassName}
        inline={inline}
        label="Top"
        value={shape.calendarShapeSize?.paddingTop}
        onChange={(paddingTop) => onChange({ calendarShapeSize: { paddingTop } })}
      />
      <NumberField
        className={fieldClassName}
        inline={inline}
        label="Bottom"
        value={shape.calendarShapeSize?.paddingBottom}
        onChange={(paddingBottom) => onChange({ calendarShapeSize: { paddingBottom } })}
      />
    </>
  );
  const dayMarginFields = (
    <>
      <NumberField
        className={fieldClassName}
        inline={inline}
        label="Left"
        value={shape.calendarShapeSize?.dayMargin?.left}
        onChange={(left) => onChange({ calendarShapeSize: { dayMargin: { left } } })}
      />
      <NumberField
        className={fieldClassName}
        inline={inline}
        label="Right"
        value={shape.calendarShapeSize?.dayMargin?.right}
        onChange={(right) => onChange({ calendarShapeSize: { dayMargin: { right } } })}
      />
      <NumberField
        className={fieldClassName}
        inline={inline}
        label="Top"
        value={shape.calendarShapeSize?.dayMargin?.top}
        onChange={(top) => onChange({ calendarShapeSize: { dayMargin: { top } } })}
      />
      <NumberField
        className={fieldClassName}
        inline={inline}
        label="Bottom"
        value={shape.calendarShapeSize?.dayMargin?.bottom}
        onChange={(bottom) => onChange({ calendarShapeSize: { dayMargin: { bottom } } })}
      />
    </>
  );

  if (inline) {
    return (
      <>
        {showPadding ? (
          <>
            {paddingLabel}: {paddingFields}
          </>
        ) : null}
        {showPadding && showDayMargin ? separator : null}
        {showDayMargin ? <>Day margin: {dayMarginFields}</> : null}
      </>
    );
  }

  return (
    <>
      {showPadding ? (
        <div>
          <strong>{paddingLabel}</strong>
          {paddingFields}
        </div>
      ) : null}
      {showDayMargin ? (
        <div>
          <strong>Day margin</strong>
          {dayMarginFields}
        </div>
      ) : null}
    </>
  );
};

export default CalendarSizeFields;
